import * as React from 'react';
import { Check, ChevronDown, X } from 'lucide-react';
import { cn } from '@/lib/utils';

interface Props {
  /** Selected option (canonical exact value). */
  value: string;
  /** Called with the canonical option string (or '' on clear). */
  onChange: (v: string) => void;
  /** Full list of allowed options. */
  options: string[];
  placeholder?: string;
  label?: string;
  /** Visual width — useful when stacked in a flex row. */
  className?: string;
  /** Disable for empty option lists. */
  disabled?: boolean;
}

/**
 * Searchable single-select combobox.
 *
 * Behavior:
 *  - Click or focus the input to open the panel (shows full list).
 *  - Typing filters by case-insensitive substring; arrow keys move highlight.
 *  - Enter picks the highlighted option, or — if there's no match — keeps the
 *    typed text but does NOT call onChange (selection must be an exact option
 *    so server-side EQ-filters keep working). Free-text search lives elsewhere.
 *  - Clicking outside closes the panel and reverts to the canonical value.
 *
 * Lists up to ~20k entries render fine because the panel only mounts visible
 * items via slicing (cap at 200 matches shown — typed query narrows quickly).
 */
export function Combobox({
  value, onChange, options,
  placeholder = 'Search…', label, className, disabled,
}: Props) {
  const [open, setOpen] = React.useState(false);
  const [draft, setDraft] = React.useState(value);
  const [hi, setHi] = React.useState(0);
  const ref = React.useRef<HTMLDivElement | null>(null);
  const listRef = React.useRef<HTMLUListElement | null>(null);

  // Keep the input synced with external value changes when panel is closed.
  React.useEffect(() => {
    if (!open) setDraft(value);
  }, [value, open]);

  // Click-outside revert + close.
  React.useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
        setDraft(value); // discard unsubmitted typing
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open, value]);

  const filtered = React.useMemo(() => {
    const q = draft.trim().toLowerCase();
    if (!q) return options.slice(0, 200);
    // Stable rank: startsWith first, then contains, capped at 200.
    const starts: string[] = [];
    const contains: string[] = [];
    for (const o of options) {
      const lo = o.toLowerCase();
      if (lo.startsWith(q)) starts.push(o);
      else if (lo.includes(q)) contains.push(o);
      if (starts.length + contains.length >= 400) break;
    }
    return [...starts, ...contains].slice(0, 200);
  }, [draft, options]);

  React.useEffect(() => { setHi(0); }, [draft, open]);

  // Auto-scroll highlighted option into view.
  React.useEffect(() => {
    if (!open || !listRef.current) return;
    const el = listRef.current.children[hi] as HTMLElement | undefined;
    el?.scrollIntoView({ block: 'nearest' });
  }, [hi, open]);

  const pick = (v: string) => {
    onChange(v);
    setDraft(v);
    setOpen(false);
  };

  return (
    <div className={cn('relative', className)} ref={ref}>
      {label && (
        <label className="mb-1 block text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
          {label}
        </label>
      )}
      <div className="relative">
        <input
          type="text"
          value={draft}
          disabled={disabled}
          placeholder={placeholder}
          onFocus={() => setOpen(true)}
          onChange={(e) => { setDraft(e.target.value); setOpen(true); }}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); setHi((h) => Math.min(filtered.length - 1, h + 1)); }
            else if (e.key === 'ArrowUp') { e.preventDefault(); setHi((h) => Math.max(0, h - 1)); }
            else if (e.key === 'Enter') {
              const opt = filtered[hi];
              if (opt) { e.preventDefault(); pick(opt); }
            }
            else if (e.key === 'Escape') { setOpen(false); setDraft(value); }
          }}
          className={cn(
            'h-10 w-full rounded-md border border-input bg-background px-3 pr-14 text-sm',
            'focus:outline-none focus:ring-2 focus:ring-ring',
            disabled && 'opacity-50',
          )}
        />
        <div className="pointer-events-none absolute right-1 top-1/2 flex -translate-y-1/2 items-center gap-1">
          {value && (
            <button
              type="button"
              onMouseDown={(e) => { e.preventDefault(); pick(''); }}
              className="pointer-events-auto rounded p-1 text-muted-foreground hover:text-ink"
              aria-label="Clear"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
          <ChevronDown className="h-4 w-4 opacity-50" />
        </div>
      </div>
      {open && (
        <div className="absolute left-0 right-0 z-30 mt-1 max-h-72 overflow-hidden rounded-md border bg-background shadow-md">
          {filtered.length === 0 ? (
            <p className="px-3 py-2 text-xs text-muted-foreground">No match</p>
          ) : (
            <ul ref={listRef} className="max-h-72 overflow-y-auto py-1">
              {filtered.map((o, i) => {
                const selected = o === value;
                const highlighted = i === hi;
                return (
                  <li
                    key={o}
                    onMouseEnter={() => setHi(i)}
                    onMouseDown={(e) => { e.preventDefault(); pick(o); }}
                    className={cn(
                      'flex cursor-pointer items-center justify-between px-3 py-1.5 text-sm',
                      highlighted && 'bg-muted',
                      selected && 'font-medium text-ink',
                    )}
                  >
                    <span className="truncate">{o}</span>
                    {selected && <Check className="ml-2 h-3.5 w-3.5 shrink-0 text-copper" />}
                  </li>
                );
              })}
              {options.length > filtered.length && draft.trim() === '' && (
                <li className="px-3 py-1 text-[10px] text-muted-foreground">
                  Showing first {filtered.length} of {options.length} — type to filter.
                </li>
              )}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
